// KontrolScore.tsx
import * as React from "react";
import {
  Box,
  Chip,
  Paper,
  Typography,
  Stack,
  Button,
  Skeleton,
  Alert,
  Collapse,
} from "@mui/material";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  fetchAssessmentFlagsByEmployeeId,
  fetchAvailabilityIndexResult,
  fetchCompanyEmployeeIds,
  fetchEmployeeCareDependents,
  fetchEmployeeScoreIdentityByAuthUserId,
  fetchEksRecommendationsByEmployeeId,
  fetchKontrolScoresByEmployeeId,
  fetchKontrolScoresByEmployeeIds,
  fetchRecommendedModulesByEmployeeId,
  getCurrentUser,
  isEmployeeModuleCompleted,
  signOut,
} from "../api/queries";
import { getOverallReadinessFromScores, readinessPercent } from "./utils/Utils";
import { useI18n } from "../i18n/I18nProvider";
import { brand } from "../theme";
import { splitModuleRecommendationsByScore } from "./scores/moduleRecommendations";
import type {
  CompanyAverageScores,
  AvailabilityIndexResult,
  EksRecommendation,
  KontrolScoreRow,
  ModuleRecommendation,
  ReadinessFlagKey,
  ReadinessFlags,
  ScoreModuleRecommendations,
  ScoreType,
} from "../types/scores";


const EKS_ACTION_LABELS: Record<
  string,
  { en: string; de: string }
> = {
  "EKS-A": {
    en: "Turn threat awareness into concrete preparedness actions.",
    de: "Setze Bedrohungsbewusstsein in konkrete Vorsorgemaßnahmen um.",
  },
  "EKS-B": {
    en: "Request or review a formal crisis or surge preparedness briefing.",
    de: "Fordere ein formelles Krisen- oder Surge-Briefing an oder prüfe es erneut.",
  },
  "EKS-C": {
    en: "Cross-train someone for your critical role responsibilities.",
    de: "Trainiere eine Vertretung für deine kritischen Rollenaufgaben ein.",
  },
  "EKS-D": {
    en: "Create and share a written personal continuity plan.",
    de: "Erstelle und teile einen schriftlichen persönlichen Kontinuitätsplan.",
  },
  "EKS-E": {
    en: "Strengthen your emergency volunteer or community response involvement.",
    de: "Stärke dein Engagement in Notfallorganisationen oder in der lokalen Krisenhilfe.",
  },
  "EKS-F": {
    en: "Document ICE contacts and a household reunion point.",
    de: "Dokumentiere Notfallkontakte und einen Treffpunkt für deinen Haushalt.",
  },
  "EKS-G": {
    en: "Prepare an alternative home heating method.",
    de: "Bereite eine alternative Heiz Möglichkeit für zuhause vor.",
  },
  "EKS-H": {
    en: "Add or test water purification capability.",
    de: "Ergänze oder teste eine Möglichkeit zur Wasseraufbereitung.",
  },
  "EKS-I": {
    en: "Rehearse your primary evacuation route.",
    de: "Übe deine primäre Evakuierungsroute praktisch.",
  },
  "EKS-J": {
    en: "Verify the nearest emergency shelter near home and work.",
    de: "Prüfe den nächstgelegenen Schutzort bei dir zuhause und am Arbeitsplatz.",
  },
};

const MODULE_PAGE_BY_ORDER: Record<number, string> = {
  1: "ModuleSupplies",
  2: "ModuleBackpack",
  3: "ModuleFirstAid",
  4: "ModuleDocuments",
  5: "ModuleCrisis",
  6: "ModuleEvacuation",
  7: "ModuleFitBody",
  8: "ModuleMentalStrength",
  9: "ModuleCommunity",
};

function getModulePageByOrder(moduleOrder: number) {
  return MODULE_PAGE_BY_ORDER[moduleOrder] ?? "ActionPlan";
}

function getEksActionLabel(code: string, lang: "en" | "de") {
  const label = EKS_ACTION_LABELS[code];
  if (!label) return code;
  return lang === "de" ? label.de : label.en;
}

function formatScore(value: number | null) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return Number(value).toFixed(2).replace(/\.00$/, "");
}

function formatPercent(value: number | null) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `${formatScore(value)}%`;
}

/** Readiness percentages read as headlines, so they round to whole numbers. */
function formatWholePercent(value: number | null) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `${Math.round(Number(value))}%`;
}

function formatDateTime(ts: string | null | undefined) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function markerPosition(value: number | null, type: ScoreType) {
  const normalizedValue = readinessPercent(value, type);
  if (normalizedValue === null) return 0;

  return normalizedValue;
}

function average(values: number[]) {
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function getTrackGradient(type: ScoreType) {
  void type;
  return "linear-gradient(90deg, #DC2626 0%, #EA580C 28%, #D6B866 52%, #84CC16 75%, #16A34A 100%)";
}

function hexToRgb(hex: string) {
  const clean = hex.replace("#", "");
  return {
    r: parseInt(clean.substring(0, 2), 16),
    g: parseInt(clean.substring(2, 4), 16),
    b: parseInt(clean.substring(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number) {
  const toHex = (n: number) => Math.round(n).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mixHex(a: string, b: string, t: number) {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);

  return rgbToHex(
    ca.r + (cb.r - ca.r) * t,
    ca.g + (cb.g - ca.g) * t,
    ca.b + (cb.b - ca.b) * t
  );
}

function colorOnScale(type: ScoreType, position: number) {
  const p = Math.max(0, Math.min(100, position));

  void type;

  const stops = [
    { at: 0, color: "#DC2626" },
    { at: 28, color: "#EA580C" },
    { at: 52, color: "#D6B866" },
    { at: 75, color: "#84CC16" },
    { at: 100, color: "#16A34A" },
  ];

  for (let i = 0; i < stops.length - 1; i += 1) {
    const current = stops[i];
    const next = stops[i + 1];

    if (p >= current.at && p <= next.at) {
      const localT = (p - current.at) / (next.at - current.at);
      return mixHex(current.color, next.color, localT);
    }
  }

  return stops[stops.length - 1].color;
}

function vividReadinessColor(position: number | null) {
  if (position === null) return brand.textFaint;

  const p = Math.max(0, Math.min(100, position));
  const stops = [
    { at: 0, color: "#FF2D2D" },
    { at: 35, color: "#FF5A1F" },
    { at: 59, color: "#FF8A00" },
    { at: 72, color: "#84CC16" },
    { at: 80, color: "#16A34A" },
    { at: 100, color: "#22C55E" },
  ];

  for (let i = 0; i < stops.length - 1; i += 1) {
    const current = stops[i];
    const next = stops[i + 1];

    if (p >= current.at && p <= next.at) {
      const localT = (p - current.at) / (next.at - current.at);
      return mixHex(current.color, next.color, localT);
    }
  }

  return stops[stops.length - 1].color;
}

/**
 * Small standing person, drawn to rest its feet on the very bottom of the
 * viewBox so it can be planted straight on top of a marker dot.
 */
function StandingFigure(props: { color: string; height?: number; title?: string }) {
  const { color, height = 32, title } = props;

  return (
    <Box
      component="svg"
      viewBox="0 0 24 29"
      role={title ? "img" : "presentation"}
      aria-label={title}
      sx={{
        height,
        width: (height * 24) / 29,
        display: "block",
        overflow: "visible",
      }}
    >
      {/* Head and body are two disjoint shapes so the white halo never cuts a
          line through the middle of the figure. */}
      <g
        fill={color}
        stroke="#FFFFFF"
        strokeWidth={1.5}
        strokeLinejoin="round"
        style={{ paintOrder: "stroke" }}
      >
        <circle cx={12} cy={4.2} r={4} />
        <path
          d="M 5.4 9.2 H 18.6 A 1.4 1.4 0 0 1 20 10.6 V 17 A 1.4 1.4 0 0 1 17.2 17 V 11
             H 16.6 V 20.2 H 15.8 V 27.5 A 1.5 1.5 0 0 1 12.8 27.5 V 21.8 H 11.2 V 27.5
             A 1.5 1.5 0 0 1 8.2 27.5 V 20.2 H 7.4 V 11 H 6.8 V 17 A 1.4 1.4 0 0 1 4 17
             V 10.6 A 1.4 1.4 0 0 1 5.4 9.2 Z"
        />
      </g>
    </Box>
  );
}

/**
 * The red-to-green readiness scale with the person standing on their own
 * marker.
 */
function ReadinessTrack(props: {
  position: number | null;
  color: string;
  onDark?: boolean;
  figureLabel?: string;
}) {
  const { position, color, onDark = false, figureLabel } = props;

  const ready = position !== null;
  const left = ready ? Math.max(0, Math.min(100, position)) : 0;

  return (
    <Box sx={{ position: "relative", pt: "40px", px: "13px", minWidth: 0 }}>
      <Box
        sx={{
          position: "relative",
          height: 18,
          borderRadius: 999,
          background: ready
            ? getTrackGradient("PBG")
            : onDark
            ? "rgba(255,255,255,0.18)"
            : brand.border,
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.24)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: `${left}%`,
            top: "50%",
            transform: "translate(-50%, -50%)",
            transition: "left 240ms ease",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              bottom: "calc(100% - 4px)",
              transform: "translateX(-50%)",
              lineHeight: 0,
            }}
          >
            <StandingFigure color={ready ? color : brand.textFaint} title={figureLabel} />
          </Box>

          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              bgcolor: ready ? color : brand.borderStrong,
              border: "3px solid #FFFFFF",
              boxShadow: "0 6px 16px rgba(17, 24, 39, 0.2)",
              transition: "background-color 240ms ease",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

/** Compact donut so a collapsed score panel still shows where it stands. */
function ScoreRing(props: { value: number | null; color: string }) {
  const { value, color } = props;

  const ready = value !== null;
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const filled = ready ? (Math.max(0, Math.min(100, value)) / 100) * circumference : 0;
  const size = { xs: 42, sm: 52 };

  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <Box
        component="svg"
        viewBox="0 0 44 44"
        sx={{ width: size, height: size, display: "block" }}
      >
        <circle cx={22} cy={22} r={radius} fill="none" stroke={brand.border} strokeWidth={4} />
        {ready ? (
          <circle
            cx={22}
            cy={22}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={4}
            strokeLinecap="round"
            strokeDasharray={`${filled} ${circumference - filled}`}
            transform="rotate(-90 22 22)"
          />
        ) : null}
      </Box>

      <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: 12, sm: 13 },
            color: ready ? brand.textPrimary : brand.textFaint,
            lineHeight: 1,
          }}
        >
          {ready ? Math.round(value) : "—"}
        </Typography>
      </Box>
    </Box>
  );
}

function getPbgResult(value: number | null, L: (en: string, de: string) => string) {
  if (value === null) {
    return {
      status: L("Not calculated yet", "Noch nicht berechnet"),
      message: L(
        "Complete the PBG assessment to see your personal threat profile.",
        "Schließe das PBG-Assessment ab, um dein persönliches Bedrohungsprofil zu sehen."
      ),
      recommendation: L("Start with the PBG assessment.", "Starte mit dem PBG-Assessment."),
    };
  }

  if (value < 5.5) {
    return {
      status: L("MASTER", "MASTER"),
      message: L(
        "Your personal threat profile is currently very stable.",
        "Dein persönliches Bedrohungsprofil ist aktuell sehr stabil."
      ),
      recommendation: L(
        "Annual digital awareness refresh.",
        "Empfohlen: jährlicher digitaler Awareness-Refresh."
      ),
    };
  }

  if (value < 7) {
    return {
      status: L("STARTER", "STARTER"),
      message: L(
        "Your profile shows a low level of exposure. Basic readiness is enough for now.",
        "Dein Profil zeigt eine niedrige Exposition. Grundlegende Vorsorge reicht aktuell aus."
      ),
      recommendation: L(
        "Planning template, 72-hour kit guidance and workshop.",
        "Empfohlen: Planungsvorlage, 72-Stunden-Kit-Anleitung und Workshop."
      ),
    };
  }

  if (value < 9) {
    return {
      status: L("PROTECTOR", "PROTECTOR"),
      message: L(
        "Your profile shows a moderate exposure pattern. A structured preparation plan is recommended.",
        "Dein Profil zeigt ein moderates Expositionsmuster. Ein strukturierter Vorsorgeplan wird empfohlen."
      ),
      recommendation: L(
        "4-week blended program covering crisis plan, communication, stress and finance.",
        "Empfohlen: 4-wöchiges Blended-Programm zu Krisenplan, Kommunikation, Stress und Finanzen."
      ),
    };
  }

  if (value < 11) {
    return {
      status: L("GUARDIAN", "GUARDIAN"),
      message: L(
        "Your profile shows elevated exposure. Prioritised action planning is recommended.",
        "Dein Profil zeigt erhöhte Exposition. Priorisierte Maßnahmenplanung wird empfohlen."
      ),
      recommendation: L(
        "8-week intensive program with drills, scenarios and crisis binder.",
        "Empfohlen: 8-wöchiges Intensivprogramm mit Übungen, Szenarien und Krisenordner."
      ),
    };
  }

  return {
    status: L("RESPONDER", "RESPONDER"),
    message: L(
      "Your profile shows critical exposure. Guided support is recommended.",
      "Dein Profil zeigt kritische Exposition. Begleitete Unterstützung wird empfohlen."
    ),
    recommendation: L(
      "Guardian program plus monthly check-ins, hotline and personal consultant.",
      "Empfohlen: Guardian-Programm plus monatliche Check-ins, Hotline und persönliche Beratung."
    ),
  };
}

function getExtPrsResult(value: number | null, L: (en: string, de: string) => string) {
  if (value === null) {
    return {
      status: L("Not calculated yet", "Noch nicht berechnet"),
      message: L(
        "Complete the Extended PRS assessment to see your practical readiness profile.",
        "Schließe das Extended-PRS-Assessment ab, um dein praktisches Vorsorgeprofil zu sehen."
      ),
      recommendation: L(
        "Start with the Extended PRS assessment.",
        "Starte mit dem Extended-PRS-Assessment."
      ),
    };
  }

  if (value <= 10) {
    return {
      status: L("Ready", "Bereit"),
      message: L(
        "Your practical readiness foundation is strong.",
        "Deine praktische Vorsorgebasis ist stark."
      ),
      recommendation: L(
        "Maintain your current readiness and refresh supplies regularly.",
        "Empfohlen: Bereitschaft aufrechterhalten und Vorräte regelmäßig prüfen."
      ),
    };
  }

  if (value <= 24) {
    return {
      status: L("Partially prepared", "Teilweise vorbereitet"),
      message: L(
        "Core basics are in place, with some practical gaps still visible.",
        "Grundlagen sind vorhanden, mit einigen sichtbaren praktischen Lücken."
      ),
      recommendation: L(
        "Close the most important household, supply and communication gaps.",
        "Empfohlen: wichtigste Haushalts-, Vorrats- und Kommunikationslücken schließen."
      ),
    };
  }

  if (value <= 38) {
    return {
      status: L("Unprepared", "Unvorbereitet"),
      message: L(
        "Several practical readiness gaps require attention.",
        "Mehrere praktische Vorsorgelücken brauchen Aufmerksamkeit."
      ),
      recommendation: L(
        "Start with essential supplies, documents, transport and communication basics.",
        "Empfohlen: beginne mit Vorräten, Dokumenten, Transport und Kommunikationsgrundlagen."
      ),
    };
  }

  return {
    status: L("Critical Gap", "Kritische Lücke"),
    message: L(
      "Your practical preparedness profile needs immediate action.",
      "Dein praktisches Vorsorgeprofil braucht sofortige Maßnahmen."
    ),
    recommendation: L(
      "Immediate guided readiness setup.",
      "Empfohlen: sofortiger begleiteter Readiness-Aufbau."
    ),
  };
}

function getEksResult(value: number | null, L: (en: string, de: string) => string) {
  if (value === null) {
    return {
      status: L("Not calculated yet", "Noch nicht berechnet"),
      message: L(
        "Complete the E Score assessment to see your engagement and continuity profile.",
        "Schließe das E-Score-Assessment ab, um dein Engagement- und Kontinuitätsprofil zu sehen."
      ),
      recommendation: L("Start with the E Score assessment.", "Starte mit dem E-Score-Assessment."),
    };
  }

  if (value >= 2.75) {
    return {
      status: L("Strong", "Stark"),
      message: L(
        "Your engagement and continuity foundation is strong.",
        "Deine Engagement- und Kontinuitätsbasis ist stark."
      ),
      recommendation: L(
        "Maintain habits and strengthen backup roles.",
        "Empfohlen: Gewohnheiten beibehalten und Backup-Rollen stärken."
      ),
    };
  }

  if (value >= 2.5) {
    return {
      status: L("Moderate", "Moderat"),
      message: L(
        "Your profile shows engagement, with documentation and continuity gaps remaining.",
        "Dein Profil zeigt Engagement, mit verbleibenden Dokumentations- und Kontinuitätslücken."
      ),
      recommendation: L(
        "Improve role backup, engagement routines and cross-training.",
        "Empfohlen: Rollen-Backup, Engagement-Routinen und Cross-Training verbessern."
      ),
    };
  }

  if (value >= 2.25) {
    return {
      status: L("Developing", "Aufbauend"),
      message: L(
        "Your engagement and continuity profile is still developing.",
        "Dein Engagement- und Kontinuitätsprofil befindet sich noch im Aufbau."
      ),
      recommendation: L(
        "Formalise continuity planning and improve engagement habits.",
        "Empfohlen: Kontinuitätsplanung formalisieren und Engagement-Routinen verbessern."
      ),
    };
  }

  return {
    status: L("Critical", "Kritisch"),
    message: L(
      "Your engagement and continuity profile needs immediate attention.",
      "Dein Engagement- und Kontinuitätsprofil braucht sofortige Aufmerksamkeit."
    ),
    recommendation: L(
      "Begin cross-training and write a continuity plan immediately.",
      "Empfohlen: sofort Cross-Training beginnen und einen Kontinuitätsplan erstellen."
    ),
  };
}

function getAviBandLabel(
  band: AvailabilityIndexResult["avi_band"] | null | undefined,
  L: (en: string, de: string) => string
) {
  if (band === "dependable") return L("Dependable", "Verlässlich");
  if (band === "conditional") return L("Conditional", "Bedingt verfügbar");
  if (band === "constrained") return L("Constrained", "Eingeschränkt");
  return L("Not calculated yet", "Noch nicht berechnet");
}

function getSummaryContent(
  userOverall: number | null,
  companyOverall: number | null,
  L: (en: string, de: string) => string
) {
  if (userOverall === null) {
    return {
      tone: "neutral" as const,
      title: L(
        "Your readiness picture is almost ready.",
        "Dein Bereitschaftsbild ist fast fertig."
      ),
      body: L(
        "Complete the three assessments below to see how your readiness profile compares with your company average.",
        "Schließe die drei Assessments unten ab, um zu sehen, wie dein Bereitschaftsprofil im Vergleich zum Unternehmensdurchschnitt aussieht."
      ),
    };
  }

  if (companyOverall === null) {
    return {
      tone: "neutral" as const,
      title: L(
        "Your readiness profile is ready.",
        "Dein Bereitschaftsprofil ist bereit."
      ),
      body: L(
        "The diagrams below show your current position across personal exposure, practical preparedness, and engagement continuity.",
        "Die Diagramme unten zeigen deine aktuelle Position in persönlicher Exposition, praktischer Vorsorge und Engagement-Kontinuität."
      ),
    };
  }

  const diff = userOverall - companyOverall;

  if (diff >= 4) {
    return {
      tone: "positive" as const,
      title: L(
        "Congrats, your score is higher than average.",
        "Glückwunsch, dein Score liegt über dem Durchschnitt."
      ),
      body: L(
        "Your overall readiness profile is currently stronger than the company average. The diagrams below show where you are already solid and where small improvements could still help.",
        "Dein gesamtes Bereitschaftsprofil ist aktuell stärker als der Unternehmensdurchschnitt. Die Diagramme unten zeigen, wo du bereits stabil bist und wo kleine Verbesserungen noch helfen können."
      ),
    };
  }

  if (diff <= -4) {
    return {
      tone: "attention" as const,
      title: L(
        "Looks like your score needs some improvement.",
        "Dein Score kann noch etwas Verbesserung gebrauchen."
      ),
      body: L(
        "Your overall readiness profile is currently below the company average, but the diagrams make the next steps easier to identify. Focus first on the areas moving toward orange or red.",
        "Dein gesamtes Bereitschaftsprofil liegt aktuell unter dem Unternehmensdurchschnitt, aber die Diagramme machen die nächsten Schritte leichter erkennbar. Starte zuerst mit den Bereichen, die Richtung Orange oder Rot gehen."
      ),
    };
  }

  return {
    tone: "neutral" as const,
    title: L(
      "Your score is close to the company average.",
      "Dein Score liegt nahe am Unternehmensdurchschnitt."
    ),
    body: L(
      "Your overall readiness profile is currently around the company average. The diagrams below show which areas are stable and which ones could be strengthened next.",
      "Dein gesamtes Bereitschaftsprofil liegt aktuell ungefähr im Unternehmensdurchschnitt. Die Diagramme unten zeigen, welche Bereiche stabil sind und welche als Nächstes gestärkt werden können."
    ),
  };
}

/**
 * Plain-language band for the overall readiness percentage, so the dial has a
 * label a reader can act on without interpreting the number itself.
 */
function getReadinessBand(value: number | null, L: (en: string, de: string) => string) {
  if (value === null) return L("Not calculated yet", "Noch nicht berechnet");
  if (value >= 80) return L("Strong readiness", "Starke Bereitschaft");
  if (value >= 60) return L("Good readiness", "Gute Bereitschaft");
  if (value >= 40) return L("Moderate readiness", "Moderate Bereitschaft");
  return L("Low readiness", "Geringe Bereitschaft");
}

/** Rounded gap to the company average, plus the wording that goes with it. */
function getComparisonNote(
  userOverall: number | null,
  companyOverall: number | null,
  L: (en: string, de: string) => string
) {
  if (userOverall === null || companyOverall === null) return null;

  const diff = Math.round(userOverall - companyOverall);

  if (diff > 0) {
    return {
      diff,
      tone: "positive" as const,
      label: L("Above team average", "Über dem Teamdurchschnitt"),
    };
  }

  if (diff < 0) {
    return {
      diff,
      tone: "attention" as const,
      label: L("Below team average", "Unter dem Teamdurchschnitt"),
    };
  }

  return {
    diff,
    tone: "neutral" as const,
    label: L("At team average", "Im Teamdurchschnitt"),
  };
}

export default function KontrolScore(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;

  const i18n: any = useI18n();
  const lang: "en" | "de" = i18n.lang ?? "en";
  const L = React.useCallback((en: string, de: string) => (lang === "de" ? de : en), [lang]);

  const UI = React.useMemo(
    () => ({
      title: L("Readiness Level", "Bereitschaftsgrad"),
      subtitle: L(
        "Your readiness profile in three independent dimensions.",
        "Dein Readiness-Profil in drei unabhängigen Dimensionen."
      ),

      loading: L("Loading your scores…", "Scores werden geladen…"),
      updated: L("Last updated", "Letztens aktualisiert"),
      retakeAssessment: L("Retake the Assessment", "Assessment wiederholen"),
      logout: L("Log out", "Abmelden"),
      checkActionPlan: L("Check Your Action Plan", "Dein Action Plan ansehen"),
      giveFeedback: L("Give feedback", "Feedback geben"),

      errNotAuth: L("Not authenticated. Please log in again.", "Nicht angemeldet. Bitte erneut einloggen."),
      errEmpNotFound: L("Employee record not found for this account.", "Kein Mitarbeiterprofil für dieses Konto gefunden."),
      errLoadScores: L("Failed to load scores.", "Scores konnten nicht geladen werden."),

      summaryLabel: L("Quick summary", "Kurze Zusammenfassung"),
      companyAverage: L("Company average", "Unternehmensdurchschnitt"),
      overallScore: L("Overall score", "Gesamtscore"),
      showScoreDetails: L("Show details", "Details anzeigen"),
      hideScoreDetails: L("Hide details", "Details ausblenden"),
      diagramsMeaning: L(
        "Tap a score area below to see its detailed profile and recommended next steps.",
        "Tippe unten auf einen Score-Bereich, um das detaillierte Profil und empfohlene nächste Schritte zu sehen."
      ),

      pbgTitle: L("PBG", "PBG"),
      pbgLabel: L("Personal Threat Level", "Persönliches Bedrohungsniveau"),
      pbgExplanation: L(
        "Shows how strongly your personal situation could be affected by a crisis. It reflects external threat perception, your role and location exposure, personal vulnerability, and how well information protects your decisions.",
        "Zeigt, wie stark deine persönliche Situation von einer Krise betroffen sein könnte. Berücksichtigt werden externe Bedrohung, Rolle und Standort, persönliche Verwundbarkeit und wie gut Informationen deine Entscheidungen schützen."
      ),

      extprsTitle: L("Extended PRS", "Extended PRS"),
      extprsLabel: L("Practical Preparedness", "Praktische Vorsorge"),
      extprsExplanation: L(
        "Shows how complete your practical crisis preparation is. It looks at energy, evacuation, documents, medical basics, food, water, cash, communication, heating, water purification, routes, and shelter awareness.",
        "Zeigt, wie vollständig deine praktische Krisenvorsorge ist. Betrachtet werden Energie, Evakuierung, Dokumente, Erste Hilfe, Lebensmittel, Wasser, Bargeld, Kommunikation, Heizen, Wasseraufbereitung, Wege und Schutzorte."
      ),

      eksTitle: L("E Score", "E Score"),
      eksLabel: L("Engagement & Continuity", "Engagement & Kontinuität"),
      eksExplanation: L(
        "Shows how actively you turn awareness into action and how resilient your work and household continuity are. It reflects habits, crisis briefings, cross-training, handover planning, volunteering, and emergency contact routines.",
        "Zeigt, wie aktiv du aus Awareness konkrete Maßnahmen machst und wie belastbar deine Arbeits- und Haushaltskontinuität ist. Berücksichtigt werden Routinen, Krisenbriefings, Cross-Training, Übergabeplanung, Ehrenamt und Notfallkontakte."
      ),

      aviTitle: L("Availability Index", "Availability Index"),
      aviLabel: L("Breakfast availability", "Breakfast-Verfügbarkeit"),
      aviExplanation: L(
        "AVI is calculated separately. It measures whether you are likely to be reachable, able and free when a continuity plan is invoked.",
        "AVI wird getrennt berechnet. Er misst, ob du wahrscheinlich erreichbar, handlungsfähig und verfügbar bist, wenn ein Kontinuitätsplan aktiviert wird."
      ),
      aviScore: L("AVI", "AVI"),
      aviOutOf: L("out of 20", "von 20"),

      noScoresTitle: L("No scores yet", "Noch keine Scores"),
      noScoresBody: L(
        "Complete the assessments to generate your readiness profile.",
        "Schließe die Assessments ab, um dein Readiness-Profil zu erzeugen."
      ),

      score: L("Score", "Score"),
      recommended: L("Recommended modules", "Empfohlene Module"),
      noRecommendedModules: L(
        "No specific module recommendation for this score yet.",
        "Für diesen Score gibt es aktuell keine spezifische Modul-Empfehlung."
      ),
      openModule: L("Open module", "Modul öffnen"),
      completed: L("Completed", "Abgeschlossen"),
      priorityEksAction: L("Priority EKS action", "Priorisierte EKS-Maßnahme"),
      additionalEksActions: L("Additional EKS actions", "Weitere EKS-Maßnahmen"),
      openAdditionalEksActions: L("Open additional EKS actions", "Weitere EKS-Maßnahmen öffnen"),
      hideAdditionalEksActions: L("Hide additional EKS actions", "Weitere EKS-Maßnahmen ausblenden"),
      noPriorityEksAction: L(
        "No priority EKS action is active right now.",
        "Aktuell ist keine priorisierte EKS-Maßnahme aktiv."
      ),

      assessmentComplete: L("Assessment complete", "Assessment abgeschlossen"),
      assessmentInProgress: L("Assessment in progress", "Assessment noch offen"),
      readinessToday: L("Your readiness today", "Deine Bereitschaft heute"),
      overallReadiness: L("Overall readiness", "Gesamtbereitschaft"),
      companyComparison: L("Company comparison", "Unternehmensvergleich"),
      yourReadiness: L("Your readiness", "Deine Bereitschaft"),
      emergencyToday: L("Today, if an emergency happened…", "Heute, wenn ein Notfall einträte …"),
      strongestArea: L("Your strongest area", "Dein stärkster Bereich"),
      biggestGap: L("Your biggest gap", "Deine größte Lücke"),

      whyThisScore: L("Why you received this score", "Warum du diesen Score hast"),
      whyThisScoreCaption: L(
        "The three readiness areas behind your overall result, ordered by how much they hold it back.",
        "Die drei Bereitschaftsbereiche hinter deinem Gesamtergebnis, sortiert danach, wie stark sie es bremsen."
      ),

      flagsTitle: L("Things you may not realise", "Was dir vielleicht nicht bewusst ist"),
      flagsCaption: L(
        "Personal risk signals derived from your assessment answers.",
        "Persönliche Risikosignale aus deinen Assessment-Antworten."
      ),
      flagApplies: L("Applies to you", "Trifft auf dich zu"),
      flagDoesNotApply: L("Does not apply to you", "Trifft nicht auf dich zu"),

      spofFlagTitle: L("Single point of failure", "Single Point of Failure"),
      spofFlagBody: L(
        "SPoF = Single Point of Failure: you are the only qualified person for a critical role or handover, so nobody is trained to cover you.",
        "SPoF = Single Point of Failure: Du bist die einzige für eine kritische Rolle oder Übergabe qualifizierte Person, niemand ist als Vertretung eingearbeitet."
      ),
      medicalFlagTitle: L("Medical risk", "Medizinisches Risiko"),
      medicalFlagBody: L(
        "Medical risk: medication, medical support, mobility-dependent health constraints that a crisis could interrupt.",
        "Medizinisches Risiko: Medikamente, medizinische Unterstützung, Mobilitätsabhängige Gesundheitsanforderungen, die eine Krise unterbrechen könnte."
      ),
      careFlagTitle: L("Care responsibility", "Pflegeverantwortung"),
      careFlagBody: L(
        "Care flag: care-dependent household members or dependents who would rely on you during a crisis.",
        "Pflege-Flag: pflegebedürftige Haushaltsmitglieder oder Angehörige, die in einer Krise auf dich angewiesen wären."
      ),

      breakdownTitle: L("Personalised Plan", "Personalisierter Plan"),
    }),
    [L]
  );

  const [loading, setLoading] = React.useState(true);
  const [uiError, setUiError] = React.useState<string | null>(null);

  const [scores, setScores] = React.useState<Record<ScoreType, KontrolScoreRow | null>>({
    PBG: null,
    Ext_PRS: null,
    E_Score: null,
  });

  const [companyAverages, setCompanyAverages] = React.useState<CompanyAverageScores>({
    PBG: null,
    Ext_PRS: null,
    E_Score: null,
  });

  const [moduleRecommendations, setModuleRecommendations] =
    React.useState<ScoreModuleRecommendations>({
      PBG: [],
      Ext_PRS: [],
      E_Score: [],
    });

  const [eksRecommendations, setEksRecommendations] = React.useState<EksRecommendation[]>([]);

  const [readinessFlags, setReadinessFlags] = React.useState<ReadinessFlags>({
    spof: false,
    medical: false,
    care: false,
  });
  const [availabilityIndex, setAvailabilityIndex] =
    React.useState<AvailabilityIndexResult | null>(null);

  const loadScores = React.useCallback(async () => {
    setLoading(true);
    setUiError(null);

    try {
      const { data: userData, error: userErr } = await getCurrentUser();

      if (userErr) throw userErr;
      if (!userData?.user) throw new Error(UI.errNotAuth);

      const { data: empRow, error: empErr } =
        await fetchEmployeeScoreIdentityByAuthUserId(userData.user.id);

      if (empErr) throw empErr;
      if (!empRow?.id) throw new Error(UI.errEmpNotFound);

      const { data: scoreRows, error: scoreErr } = await fetchKontrolScoresByEmployeeId(empRow.id);

      if (scoreErr) throw scoreErr;

      const { data: availabilityRow, error: availabilityErr } =
        await fetchAvailabilityIndexResult(empRow.id);

      if (availabilityErr) {
        console.warn("Could not load availability index result.", availabilityErr);
      }

      const nextScores: Record<ScoreType, KontrolScoreRow | null> = {
        PBG: null,
        Ext_PRS: null,
        E_Score: null,
      };

      for (const row of (scoreRows ?? []) as KontrolScoreRow[]) {
        nextScores[row.score_type] = {
          score_type: row.score_type,
          kontrol_score:
            typeof row.kontrol_score === "number"
              ? row.kontrol_score
              : Number(row.kontrol_score ?? 0),
          updated_at: row.updated_at,
        };
      }

      const { data: moduleRecommendationRows, error: moduleRecommendationErr } =
        await fetchRecommendedModulesByEmployeeId(empRow.id);

      if (moduleRecommendationErr) throw moduleRecommendationErr;

      const nextModuleRecommendations: ModuleRecommendation[] = await Promise.all(
        (moduleRecommendationRows ?? []).map(async (row: any) => {
          let isCompleted = false;

          if (row.module_id) {
            const { data: completedData, error: completedErr } =
              await isEmployeeModuleCompleted(empRow.id, row.module_id);

            if (completedErr) throw completedErr;
            isCompleted = Boolean(completedData);
          }

          return {
            module_id: row.module_id,
            module_order: Number(row.module_order),
            name_en: row.modules?.name_en ?? `Module ${row.module_order}`,
            name_de: row.modules?.name_de ?? `Modul ${row.module_order}`,
            trigger_reasons: Array.isArray(row.trigger_reasons) ? row.trigger_reasons : [],
            is_completed: isCompleted,
          };
        })
      );

      const { data: eksRecommendationRows, error: eksRecommendationErr } =
        await fetchEksRecommendationsByEmployeeId(empRow.id);

      if (eksRecommendationErr) throw eksRecommendationErr;

      const nextEksRecommendations: EksRecommendation[] = (eksRecommendationRows ?? []).map(
        (row: any) => ({
          eks_module_code: row.eks_module_code,
          question_code: row.question_code,
          trigger_answer: row.trigger_answer,
          is_priority: Boolean(row.is_priority),
          is_recommended: Boolean(row.is_recommended),
        })
      );

      const [
        { data: flagRow, error: flagErr },
        { data: socialRow, error: socialErr },
      ] = await Promise.all([
        fetchAssessmentFlagsByEmployeeId(empRow.id),
        fetchEmployeeCareDependents(empRow.id),
      ]);

      if (flagErr) throw flagErr;
      if (socialErr) throw socialErr;

      // `spof` and `medical` are derived server-side; the care flag mirrors the
      // manager dashboard, which reads it straight off the social profile.
      const careCount = (socialRow as any)?.people_needing_care_count ?? null;

      const nextReadinessFlags: ReadinessFlags = {
        spof: (flagRow as any)?.spof === true,
        medical: (flagRow as any)?.medical === true,
        care: careCount !== null && String(careCount) !== "0",
      };

      let nextCompanyAverages: CompanyAverageScores = {
        PBG: null,
        Ext_PRS: null,
        E_Score: null,
      };

      if (empRow.company_id) {
        const { data: companyEmployees, error: companyEmployeesErr } =
          await fetchCompanyEmployeeIds(empRow.company_id);

        if (companyEmployeesErr) throw companyEmployeesErr;

        const companyEmployeeIds = (companyEmployees ?? [])
          .map((employee: any) => employee.id)
          .filter(Boolean);

        if (companyEmployeeIds.length > 0) {
          const { data: companyScoreRows, error: companyScoreErr } =
            await fetchKontrolScoresByEmployeeIds(companyEmployeeIds);

          if (companyScoreErr) throw companyScoreErr;

          const grouped: Record<ScoreType, number[]> = {
            PBG: [],
            Ext_PRS: [],
            E_Score: [],
          };

          for (const row of companyScoreRows ?? []) {
            const type = row.score_type as ScoreType;
            const value = Number(row.kontrol_score);

            if (["PBG", "Ext_PRS", "E_Score"].includes(type) && !Number.isNaN(value)) {
              grouped[type].push(value);
            }
          }

          nextCompanyAverages = {
            PBG: average(grouped.PBG),
            Ext_PRS: average(grouped.Ext_PRS),
            E_Score: average(grouped.E_Score),
          };
        }
      }

      setScores(nextScores);
      setAvailabilityIndex(
        availabilityErr ? null : (availabilityRow as AvailabilityIndexResult | null) ?? null
      );
      setCompanyAverages(nextCompanyAverages);
      setReadinessFlags(nextReadinessFlags);
      setModuleRecommendations(splitModuleRecommendationsByScore(nextModuleRecommendations));
      setEksRecommendations(nextEksRecommendations);
    } catch (e: any) {
      setUiError(e?.message ?? UI.errLoadScores);
    } finally {
      setLoading(false);
    }
  }, [UI.errNotAuth, UI.errEmpNotFound, UI.errLoadScores]);

  React.useEffect(() => {
    loadScores();
  }, [loadScores]);

  const handleLogout = async () => {
    // Signs out whatever local session is stuck here -- e.g. the account
    // was deleted after this browser last logged in, so every fetch here
    // fails and there'd otherwise be no way back to a working login screen.
    await signOut();
    localStorage.removeItem("tk.currentPage");
    localStorage.removeItem("tk.nav.role");
    handlePageChange("Entry");
  };

  const pbgResult = getPbgResult(scores.PBG?.kontrol_score ?? null, L);
  const extprsResult = getExtPrsResult(scores.Ext_PRS?.kontrol_score ?? null, L);
  const eksResult = getEksResult(scores.E_Score?.kontrol_score ?? null, L);
  const aviRaw =
    availabilityIndex?.avi_raw === null || availabilityIndex?.avi_raw === undefined
      ? null
      : Number(availabilityIndex.avi_raw);
  const aviBandLabel = getAviBandLabel(availabilityIndex?.avi_band, L);

  const currentScoreValues: Record<ScoreType, number | null> = {
    PBG: scores.PBG?.kontrol_score ?? null,
    Ext_PRS: scores.Ext_PRS?.kontrol_score ?? null,
    E_Score: scores.E_Score?.kontrol_score ?? null,
  };

  const userOverallReadiness = getOverallReadinessFromScores(currentScoreValues);
  const companyOverallReadiness = getOverallReadinessFromScores(companyAverages);
  const summary = getSummaryContent(userOverallReadiness, companyOverallReadiness, L);

  const scoreRows = [
    {
      type: "PBG" as ScoreType,
      title: UI.pbgTitle,
      label: UI.pbgLabel,
      explanation: UI.pbgExplanation,
      value: scores.PBG?.kontrol_score ?? null,
      updatedAt: scores.PBG?.updated_at ?? null,
      moduleRecommendations: moduleRecommendations.PBG,
      ...pbgResult,
    },
    {
      type: "Ext_PRS" as ScoreType,
      title: UI.extprsTitle,
      label: UI.extprsLabel,
      explanation: UI.extprsExplanation,
      value: scores.Ext_PRS?.kontrol_score ?? null,
      updatedAt: scores.Ext_PRS?.updated_at ?? null,
      moduleRecommendations: moduleRecommendations.Ext_PRS,
      ...extprsResult,
    },
    {
      type: "E_Score" as ScoreType,
      title: UI.eksTitle,
      label: UI.eksLabel,
      explanation: UI.eksExplanation,
      value: scores.E_Score?.kontrol_score ?? null,
      updatedAt: scores.E_Score?.updated_at ?? null,
      moduleRecommendations: moduleRecommendations.E_Score,
      eksRecommendations,
      ...eksResult,
    },
  ];

  const completedCount = scoreRows.filter((c) => c.value !== null).length;
  const allAssessmentsDone = completedCount === scoreRows.length;

  const band = getReadinessBand(userOverallReadiness, L);

  const lastUpdatedAt =
    scoreRows
      .map((row) => row.updatedAt)
      .filter((ts): ts is string => Boolean(ts))
      .sort()
      .pop() ?? null;
  const comparisonNote = getComparisonNote(userOverallReadiness, companyOverallReadiness, L);

  // Worst readiness first: that ordering drives both the ranked impact card and
  // the two "if it happened today" takeaways.
  const rankedScoreRows = scoreRows
    .map((row) => ({
      type: row.type,
      title: row.title,
      label: row.label,
      status: row.status,
      message: row.message,
      percent: readinessPercent(row.value, row.type),
    }))
    .sort((a, b) => (a.percent ?? Infinity) - (b.percent ?? Infinity));

  const scoredRankedRows = rankedScoreRows.filter((row) => row.percent !== null);
  const weakestArea = scoredRankedRows[0] ?? null;
  const strongestArea =
    scoredRankedRows.length > 1 ? scoredRankedRows[scoredRankedRows.length - 1] : null;

  const flagCards: {
    key: ReadinessFlagKey;
    title: string;
    description: string;
    color: string;
    active: boolean;
  }[] = [
    {
      key: "spof",
      title: UI.spofFlagTitle,
      description: UI.spofFlagBody,
      color: "#EA580C",
      active: readinessFlags.spof,
    },
    {
      key: "medical",
      title: UI.medicalFlagTitle,
      description: UI.medicalFlagBody,
      color: "#DC2626",
      active: readinessFlags.medical,
    },
    {
      key: "care",
      title: UI.careFlagTitle,
      description: UI.careFlagBody,
      color: "#7C3AED",
      active: readinessFlags.care,
    },
  ];

  const openRecommendedModule = React.useCallback(
    (moduleOrder: number) => {
      handlePageChange(getModulePageByOrder(moduleOrder));
    },
    [handlePageChange]
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: brand.page, p: { xs: 2, md: 3 } }}>
      <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                bgcolor: brand.surface,
                border: `1px solid ${brand.border}`,
                display: "grid",
                placeItems: "center",
                color: brand.ink,
                flexShrink: 0,
              }}
            >
              <TuneOutlinedIcon />
            </Box>

            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 900,
                  color: brand.textPrimary,
                  lineHeight: 1.1,
                }}
              >
                {UI.title}
              </Typography>

              <Typography variant="body2" sx={{ color: brand.textMuted }}>
                {UI.subtitle}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{
              width: { xs: "100%", md: "auto" },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => handlePageChange("ReadinessCheck")}
              sx={{
                ...btnOutlineSx,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 220 },
              }}
            >
              {UI.retakeAssessment}
            </Button>

            <Button
              variant="outlined"
              startIcon={<RateReviewOutlinedIcon />}
              onClick={() => handlePageChange("ScoreFeedback")}
              sx={{
                ...btnOutlineSx,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 180 },
              }}
            >
              {UI.giveFeedback}
            </Button>

            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => handlePageChange("ActionPlan")}
              sx={{
                ...btnPrimarySx,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 220 },
              }}
            >
              {UI.checkActionPlan}
            </Button>
          </Stack>
        </Stack>

        {uiError && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2.5 }}
            action={
              <Button color="inherit" size="small" onClick={handleLogout} sx={{ fontWeight: 900 }}>
                {UI.logout}
              </Button>
            }
          >
            {uiError}
          </Alert>
        )}

        {loading ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={300} />
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Skeleton variant="rounded" height={240} sx={{ flex: 1 }} />
              <Skeleton variant="rounded" height={240} sx={{ flex: 1 }} />
            </Stack>
            <Skeleton variant="rounded" height={96} />
            <Skeleton variant="rounded" height={96} />
            <Skeleton variant="rounded" height={96} />
          </Stack>
        ) : (
          <Stack spacing={2}>
            <ReadinessHero
              summary={summary}
              summaryLabel={UI.summaryLabel}
              readinessTodayLabel={UI.readinessToday}
              overallReadinessLabel={UI.overallReadiness}
              statusLabel={
                allAssessmentsDone ? UI.assessmentComplete : UI.assessmentInProgress
              }
              statusDone={allAssessmentsDone}
              band={band}
              updatedLabel={UI.updated}
              lastUpdatedAt={lastUpdatedAt}
              userOverallReadiness={userOverallReadiness}
              companyOverallReadiness={companyOverallReadiness}
              companyComparisonLabel={UI.companyComparison}
              yourReadinessLabel={UI.yourReadiness}
              companyAverageLabel={UI.companyAverage}
              comparisonNote={comparisonNote}
              emergencyTodayLabel={UI.emergencyToday}
              strongestAreaLabel={UI.strongestArea}
              biggestGapLabel={UI.biggestGap}
              strongest={strongestArea}
              weakest={weakestArea}
              noScoresBody={UI.noScoresBody}
            />

            <AvailabilityIndexCard
              title={UI.aviTitle}
              label={UI.aviLabel}
              explanation={UI.aviExplanation}
              scoreLabel={UI.aviScore}
              outOfLabel={UI.aviOutOf}
              bandLabel={aviBandLabel}
              value={aviRaw}
              updatedLabel={UI.updated}
              updatedAt={availabilityIndex?.updated_at ?? null}
            />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
              <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
                <ScoreImpactCard
                  title={UI.whyThisScore}
                  caption={UI.whyThisScoreCaption}
                  rows={rankedScoreRows}
                  emptyLabel={UI.noScoresBody}
                />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
                <ReadinessFlagsCard
                  title={UI.flagsTitle}
                  caption={UI.flagsCaption}
                  flags={flagCards}
                  activeLabel={UI.flagApplies}
                  inactiveLabel={UI.flagDoesNotApply}
                />
              </Box>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${brand.border}`,
                bgcolor: brand.surface,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 2.5 },
                  pt: { xs: 2, md: 2.25 },
                  pb: 1.5,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 2,
                      bgcolor: brand.surfaceSunken,
                      border: `1px solid ${brand.border}`,
                      display: "grid",
                      placeItems: "center",
                      color: brand.textSecondary,
                      flexShrink: 0,
                    }}
                  >
                    <LayersOutlinedIcon fontSize="small" />
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900, color: brand.textPrimary, fontSize: 17 }}>
                      {UI.breakdownTitle}
                    </Typography>

                    <Typography variant="body2" sx={{ color: brand.textMuted, mt: 0.25 }}>
                      {UI.diagramsMeaning}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ px: { xs: 2, md: 2.5 }, pb: { xs: 2, md: 2.5 } }}>
                <Stack spacing={1.5}>
                  {scoreRows.map((row) => (
                    <ScoreLine
                      key={row.type}
                      data={row}
                      updated={UI.updated}
                      scoreLabel={UI.score}
                      showDetailsLabel={UI.showScoreDetails}
                      hideDetailsLabel={UI.hideScoreDetails}
                      recommendedLabel={UI.recommended}
                      noRecommendedModulesLabel={UI.noRecommendedModules}
                      openModuleLabel={UI.openModule}
                      completedLabel={UI.completed}
                      priorityEksActionLabel={UI.priorityEksAction}
                      additionalEksActionsLabel={UI.additionalEksActions}
                      openAdditionalEksActionsLabel={UI.openAdditionalEksActions}
                      hideAdditionalEksActionsLabel={UI.hideAdditionalEksActions}
                      noPriorityEksActionLabel={UI.noPriorityEksAction}
                      lang={lang}
                      onOpenModule={openRecommendedModule}
                    />
                  ))}
                </Stack>

                {completedCount === 0 && (
                  <Alert severity="info" sx={{ mt: 2, borderRadius: 2.5 }}>
                    <Typography sx={{ fontWeight: 900 }}>{UI.noScoresTitle}</Typography>
                    <Typography variant="body2">{UI.noScoresBody}</Typography>
                  </Alert>
                )}
              </Box>
            </Paper>
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function AvailabilityIndexCard(props: {
  title: string;
  label: string;
  explanation: string;
  scoreLabel: string;
  outOfLabel: string;
  bandLabel: string;
  value: number | null;
  updatedLabel: string;
  updatedAt: string | null;
}) {
  const value = props.value;
  const ready = value !== null && !Number.isNaN(value);
  const pct = ready ? Math.max(0, Math.min(100, (value / 20) * 100)) : 0;
  const color = ready ? colorOnScale("PBG", pct) : brand.textFaint;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.surface,
        p: { xs: 2, md: 2.5 },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: `${color}16`,
              border: `1px solid ${ready ? `${color}66` : brand.border}`,
              color,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <PendingOutlinedIcon fontSize="small" />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, color: brand.textPrimary, fontSize: 17 }}>
              {props.title}
            </Typography>
            <Typography sx={{ fontWeight: 900, color, mt: 0.25 }}>
              {props.bandLabel}
            </Typography>
            <Typography variant="body2" sx={{ color: brand.textMuted, mt: 0.5, maxWidth: 760 }}>
              {props.explanation}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ minWidth: { md: 220 } }}>
          <Typography sx={{ fontSize: 11, fontWeight: 900, color: brand.textFaint, textTransform: "uppercase" }}>
            {props.scoreLabel}
          </Typography>
          <Typography sx={{ fontWeight: 950, color: brand.textPrimary, fontSize: 34, lineHeight: 1 }}>
            {ready ? formatScore(props.value ?? null) : "—"}
            <Typography component="span" sx={{ ml: 0.75, fontWeight: 900, fontSize: 14, color: brand.textMuted }}>
              {props.outOfLabel}
            </Typography>
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 12, fontWeight: 800, color: brand.textFaint }}>
            {props.updatedLabel}: {formatDateTime(props.updatedAt)}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function DialTicks(props: { color: string; value: number | null }) {
  const { color, value } = props;
  const ready = value !== null;
  const pct = ready ? Math.max(0, Math.min(100, value)) : 0;
  const ticks = Array.from({ length: 72 }, (_, index) => index);

  return (
    <Box
      component="svg"
      viewBox="0 0 220 220"
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    >
      {ticks.map((tick) => {
        const progress = ((tick + 1) / ticks.length) * 100;
        if (!ready || progress > pct) return null;

        const angle = tick * (360 / ticks.length) - 90;
        const radians = (angle * Math.PI) / 180;
        const tickLength = tick % 4 === 0 ? 8 : 5;
        const innerRadius = 94;
        const outerRadius = innerRadius + tickLength;
        const x1 = 110 + innerRadius * Math.cos(radians);
        const y1 = 110 + innerRadius * Math.sin(radians);
        const x2 = 110 + outerRadius * Math.cos(radians);
        const y2 = 110 + outerRadius * Math.sin(radians);

        return (
          <line
            key={tick}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={color}
            strokeWidth={tick % 4 === 0 ? 2.2 : 1.5}
            strokeLinecap="round"
            opacity={tick % 4 === 0 ? 0.62 : 0.36}
          />
        );
      })}
    </Box>
  );
}

function ReadinessDial(props: {
  value: number | null;
  color: string;
  label: string;
  band: string;
}) {
  const { value, color, label, band } = props;

  const ready = value !== null;
  const radius = 76;
  const circumference = 2 * Math.PI * radius;
  const filled = ready ? (Math.max(0, Math.min(100, value)) / 100) * circumference : 0;
  const dialSize = { xs: 214, sm: 244 };
  const shellHeight = { xs: 254, sm: 286 };

  return (
    <Box
      sx={{
        position: "relative",
        width: dialSize,
        height: shellHeight,
        flexShrink: 0,
        filter: ready ? `drop-shadow(0 18px 28px ${color}26)` : "none",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: "0 0 auto",
          width: dialSize,
          height: dialSize,
        }}
      >
        <DialTicks color={color} value={value} />

        <Box
          component="svg"
          viewBox="0 0 220 220"
          sx={{ position: "relative", width: "100%", height: "100%", display: "block", zIndex: 1 }}
        >
          <defs>
            <filter id="readinessDialGlow" x="-35%" y="-35%" width="170%" height="170%">
              <feGaussianBlur stdDeviation="4.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <circle
            cx={110}
            cy={110}
            r={radius}
            fill="none"
            stroke="rgba(17,24,39,0.12)"
            strokeWidth={15}
            strokeLinecap="round"
            transform="rotate(-90 110 110)"
          />

          {ready ? (
            <circle
              cx={110}
              cy={110}
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth={15}
              strokeLinecap="round"
              strokeDasharray={`${filled} ${circumference - filled}`}
              transform="rotate(-90 110 110)"
              filter="url(#readinessDialGlow)"
              style={{ transition: "stroke-dasharray 320ms ease, stroke 320ms ease" }}
            />
          ) : null}
        </Box>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            textAlign: "center",
            px: 2,
            zIndex: 2,
          }}
        >
          <Stack spacing={0.75} alignItems="center">
            <Typography
              sx={{
                fontWeight: 950,
                fontSize: { xs: 50, sm: 60 },
                lineHeight: 0.92,
                color: ready ? brand.textPrimary : brand.textFaint,
                textShadow: ready ? "0 3px 14px rgba(255,255,255,0.9)" : "none",
              }}
            >
              {ready ? Math.round(value) : "—"}
              {ready ? (
                <Box
                  component="span"
                  sx={{
                    ml: 0.25,
                    fontSize: { xs: 21, sm: 25 },
                    color,
                    verticalAlign: "baseline",
                  }}
                >
                  %
                </Box>
              ) : null}
            </Typography>

            <Typography
              sx={{
                maxWidth: { xs: 116, sm: 132 },
                color: ready ? brand.textMuted : brand.textFaint,
                fontSize: { xs: 10, sm: 11 },
                fontWeight: 950,
                lineHeight: 1.12,
                textTransform: "uppercase",
              }}
            >
              {label}
            </Typography>
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          position: "absolute",
          left: "50%",
          bottom: 0,
          transform: "translateX(-50%)",
          maxWidth: "88%",
          px: 1.65,
          py: 0.7,
          borderRadius: 999,
          border: `1px solid ${ready ? `${color}66` : brand.border}`,
          bgcolor: ready ? `${color}1F` : brand.surfaceMuted,
          boxShadow: ready ? `0 8px 18px ${color}22, inset 0 0 0 1px rgba(255,255,255,0.38)` : "none",
          zIndex: 2,
        }}
      >
        <Typography
          noWrap
          sx={{
            fontWeight: 950,
            fontSize: { xs: 12, sm: 13 },
            color: ready ? color : brand.textFaint,
            lineHeight: 1.1,
          }}
        >
          {band}
        </Typography>
      </Box>
    </Box>
  );
}

/** Flat comparison bar used by the company panel in the hero. */
function ComparisonBar(props: { label: string; value: number | null; color: string }) {
  const { label, value, color } = props;
  const ready = value !== null;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="body2" sx={{ color: brand.textSecondary, fontWeight: 800 }}>
          {label}
        </Typography>

        <Typography variant="body2" sx={{ color: brand.textPrimary, fontWeight: 900 }}>
          {formatWholePercent(value)}
        </Typography>
      </Stack>

      <Box
        sx={{
          mt: 0.65,
          height: 8,
          borderRadius: 999,
          bgcolor: brand.borderSoft,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${ready ? Math.max(0, Math.min(100, value)) : 0}%`,
            borderRadius: 999,
            bgcolor: ready ? color : brand.borderStrong,
            transition: "width 320ms ease",
          }}
        />
      </Box>
    </Box>
  );
}

/**
 * The page hero: the overall dial, the plain-language summary with the two
 * "if it happened today" takeaways, and the company comparison panel.
 */
function ReadinessHero(props: {
  summary: { tone: "positive" | "attention" | "neutral"; title: string; body: string };
  summaryLabel: string;
  readinessTodayLabel: string;
  overallReadinessLabel: string;
  statusLabel: string;
  statusDone: boolean;
  band: string;
  updatedLabel: string;
  lastUpdatedAt: string | null;
  userOverallReadiness: number | null;
  companyOverallReadiness: number | null;
  companyComparisonLabel: string;
  yourReadinessLabel: string;
  companyAverageLabel: string;
  comparisonNote: { label: string; diff: number; tone: "positive" | "attention" | "neutral" } | null;
  emergencyTodayLabel: string;
  strongestAreaLabel: string;
  biggestGapLabel: string;
  strongest: { label: string; message: string; percent: number | null } | null;
  weakest: { label: string; message: string; percent: number | null } | null;
  noScoresBody: string;
}) {
  const {
    summary,
    summaryLabel,
    readinessTodayLabel,
    overallReadinessLabel,
    statusLabel,
    statusDone,
    band,
    updatedLabel,
    lastUpdatedAt,
    userOverallReadiness,
    companyOverallReadiness,
    companyComparisonLabel,
    yourReadinessLabel,
    companyAverageLabel,
    comparisonNote,
    emergencyTodayLabel,
    strongestAreaLabel,
    biggestGapLabel,
    strongest,
    weakest,
    noScoresBody,
  } = props;

  const overallColor = vividReadinessColor(userOverallReadiness);

  const toneColor =
    summary.tone === "positive" ? "#15803D" : summary.tone === "attention" ? "#C2410C" : brand.ink;

  const noteColor =
    comparisonNote === null
      ? brand.textMuted
      : comparisonNote.tone === "positive"
      ? "#15803D"
      : comparisonNote.tone === "attention"
      ? "#C2410C"
      : brand.textSecondary;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.surface,
        overflow: "hidden",
        boxShadow: "0 18px 45px rgba(17,24,39,0.06)",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 1.25,
          borderBottom: `1px solid ${brand.border}`,
          bgcolor: brand.surfaceMuted,
        }}
      >
        <Chip
          size="small"
          icon={statusDone ? <TaskAltIcon /> : <PendingOutlinedIcon />}
          label={statusLabel}
          sx={{
            fontWeight: 900,
            borderRadius: 999,
            border: `1px solid ${statusDone ? "#A7F3D0" : brand.borderStrong}`,
            bgcolor: statusDone ? "#ECFDF5" : brand.surface,
            color: statusDone ? "#047857" : brand.textSecondary,
            "& .MuiChip-icon": { color: "inherit" },
          }}
        />

        {lastUpdatedAt ? (
          <Typography variant="caption" sx={{ color: brand.textFaint, fontWeight: 800 }}>
            {updatedLabel}: {formatDateTime(lastUpdatedAt)}
          </Typography>
        ) : null}
      </Stack>

      <Box sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={{ xs: 2.5, lg: 3 }}
          alignItems={{ xs: "stretch", lg: "flex-start" }}
        >
          <Stack
            spacing={1.25}
            alignItems="center"
            sx={{ flexShrink: 0, alignSelf: { xs: "center", lg: "flex-start" } }}
          >
            <Typography
              variant="caption"
              sx={{
                color: brand.textFaint,
                fontWeight: 900,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                textAlign: "center",
              }}
            >
              {readinessTodayLabel}
            </Typography>

            <ReadinessDial
              value={userOverallReadiness}
              color={overallColor}
              label={overallReadinessLabel}
              band={band}
            />
          </Stack>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: brand.textFaint,
                fontWeight: 900,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                mb: 0.35,
              }}
            >
              {summaryLabel}
            </Typography>

            <Typography sx={{ fontWeight: 900, color: toneColor, fontSize: { xs: 19, sm: 21 } }}>
              {summary.title}
            </Typography>

            <Typography variant="body2" sx={{ color: brand.textSecondary, mt: 0.75, lineHeight: 1.65 }}>
              {summary.body}
            </Typography>

            <Box
              sx={{
                mt: 1.75,
                borderRadius: 2.5,
                border: `1px solid ${brand.border}`,
                bgcolor: brand.surfaceSunken,
                p: { xs: 1.5, md: 1.75 },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.75,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: brand.surface,
                    border: `1px solid ${brand.border}`,
                    color: brand.accentStrong,
                    flexShrink: 0,
                  }}
                >
                  <ShieldOutlinedIcon sx={{ fontSize: 17 }} />
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    color: brand.textSecondary,
                  }}
                >
                  {emergencyTodayLabel}
                </Typography>
              </Stack>

              {strongest || weakest ? (
                <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                  {strongest ? (
                    <EmergencyTakeaway
                      tone="positive"
                      caption={strongestAreaLabel}
                      area={strongest.label}
                      percent={strongest.percent}
                      message={strongest.message}
                    />
                  ) : null}

                  {weakest ? (
                    <EmergencyTakeaway
                      tone="attention"
                      caption={biggestGapLabel}
                      area={weakest.label}
                      percent={weakest.percent}
                      message={weakest.message}
                    />
                  ) : null}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: brand.textMuted, mt: 1, lineHeight: 1.6 }}>
                  {noScoresBody}
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${brand.border}`,
              bgcolor: brand.surfaceMuted,
              p: { xs: 1.75, md: 2 },
              width: { xs: "100%", lg: 280 },
              flexShrink: 0,
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <PeopleAltOutlinedIcon sx={{ fontSize: 16, color: brand.textFaint }} />

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 900,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: brand.textFaint,
                }}
              >
                {companyComparisonLabel}
              </Typography>
            </Stack>

            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              <ComparisonBar
                label={yourReadinessLabel}
                value={userOverallReadiness}
                color={overallColor}
              />

              <ComparisonBar
                label={companyAverageLabel}
                value={companyOverallReadiness}
                color={brand.borderStrong}
              />
            </Stack>

            {comparisonNote ? (
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ mt: 1.75, pt: 1.5, borderTop: `1px solid ${brand.border}` }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.35,
                    px: 0.85,
                    py: 0.25,
                    borderRadius: 999,
                    bgcolor: `${noteColor}14`,
                    color: noteColor,
                    fontWeight: 900,
                    fontSize: 12,
                    flexShrink: 0,
                  }}
                >
                  <TrendingUpOutlinedIcon
                    sx={{
                      fontSize: 14,
                      transform: comparisonNote.diff < 0 ? "scaleY(-1)" : "none",
                    }}
                  />
                  {comparisonNote.diff > 0 ? "+" : ""}
                  {comparisonNote.diff}
                </Box>

                <Typography variant="body2" sx={{ color: noteColor, fontWeight: 900 }}>
                  {comparisonNote.label}
                </Typography>
              </Stack>
            ) : null}
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}

/** One "if it happened today" line: a strength or a gap, with its own tone. */
function EmergencyTakeaway(props: {
  tone: "positive" | "attention";
  caption: string;
  area: string;
  percent: number | null;
  message: string;
}) {
  const { tone, caption, area, percent, message } = props;

  const color = tone === "positive" ? "#15803D" : "#C2410C";
  const bg = tone === "positive" ? "#ECFDF5" : "#FFF7ED";
  const border = tone === "positive" ? "#A7F3D0" : "#FED7AA";

  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: 1.5,
          display: "grid",
          placeItems: "center",
          bgcolor: bg,
          border: `1px solid ${border}`,
          color,
          flexShrink: 0,
          mt: 0.2,
        }}
      >
        {tone === "positive" ? (
          <ThumbUpOutlinedIcon sx={{ fontSize: 14 }} />
        ) : (
          <WarningAmberOutlinedIcon sx={{ fontSize: 15 }} />
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 900,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color,
          }}
        >
          {caption}: {area} · {formatWholePercent(percent)}
        </Typography>

        <Typography variant="body2" sx={{ color: brand.textSecondary, lineHeight: 1.55, mt: 0.15 }}>
          {message}
        </Typography>
      </Box>
    </Stack>
  );
}

/** "Why you received this score": the three areas ranked by how much they drag. */
function ScoreImpactCard(props: {
  title: string;
  caption: string;
  rows: {
    type: ScoreType;
    title: string;
    label: string;
    status: string;
    percent: number | null;
  }[];
  emptyLabel: string;
}) {
  const { title, caption, rows, emptyLabel } = props;
  const scored = rows.filter((row) => row.percent !== null);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.surface,
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box sx={{ p: { xs: 2, md: 2.25 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              bgcolor: brand.surfaceSunken,
              border: `1px solid ${brand.border}`,
              display: "grid",
              placeItems: "center",
              color: brand.textSecondary,
              flexShrink: 0,
            }}
          >
            <HelpOutlineIcon fontSize="small" />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, color: brand.textPrimary, fontSize: 17 }}>
              {title}
            </Typography>

            <Typography variant="body2" sx={{ color: brand.textMuted, mt: 0.25 }}>
              {caption}
            </Typography>
          </Box>
        </Stack>

        {scored.length === 0 ? (
          <Typography variant="body2" sx={{ color: brand.textMuted, mt: 1.75, lineHeight: 1.6 }}>
            {emptyLabel}
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mt: 1.75 }}>
            {scored.map((row, index) => {
              const color = colorOnScale(row.type, row.percent as number);

              return (
                <Stack
                  key={row.type}
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{
                    borderRadius: 2.25,
                    border: `1px solid ${brand.border}`,
                    bgcolor: brand.surfaceMuted,
                    px: 1.25,
                    py: 1.1,
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: brand.surface,
                      border: `1px solid ${brand.borderStrong}`,
                      color: brand.textSecondary,
                      fontWeight: 900,
                      fontSize: 12,
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{ fontWeight: 900, color: brand.textPrimary, lineHeight: 1.3, fontSize: 15 }}
                    >
                      {row.label}
                    </Typography>

                    <Typography variant="caption" sx={{ color: brand.textMuted, fontWeight: 800 }}>
                      {row.title} · {row.status}
                    </Typography>
                  </Box>

                  <Typography sx={{ fontWeight: 900, color, fontSize: 16, flexShrink: 0 }}>
                    {formatWholePercent(row.percent)}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

/**
 * "Things you may not realise": the personal risk flags. Every flag is always
 * listed so the explanation stays readable; the ones that do not apply are
 * rendered in a muted, clearly inactive state instead of being hidden.
 */
function ReadinessFlagsCard(props: {
  title: string;
  caption: string;
  flags: {
    key: ReadinessFlagKey;
    title: string;
    description: string;
    color: string;
    active: boolean;
  }[];
  activeLabel: string;
  inactiveLabel: string;
}) {
  const { title, caption, flags, activeLabel, inactiveLabel } = props;

  const icons: Record<ReadinessFlagKey, React.ReactNode> = {
    spof: <WarningAmberOutlinedIcon sx={{ fontSize: 17 }} />,
    medical: <MedicalServicesOutlinedIcon sx={{ fontSize: 17 }} />,
    care: <GroupsOutlinedIcon sx={{ fontSize: 17 }} />,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.surface,
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box sx={{ p: { xs: 2, md: 2.25 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              bgcolor: brand.surfaceSunken,
              border: `1px solid ${brand.border}`,
              display: "grid",
              placeItems: "center",
              color: brand.textSecondary,
              flexShrink: 0,
            }}
          >
            <ReportProblemOutlinedIcon fontSize="small" />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, color: brand.textPrimary, fontSize: 17 }}>
              {title}
            </Typography>

            <Typography variant="body2" sx={{ color: brand.textMuted, mt: 0.25 }}>
              {caption}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1} sx={{ mt: 1.75 }}>
          {flags.map((flag) => (
            <Box
              key={flag.key}
              aria-label={`${flag.title}: ${flag.active ? activeLabel : inactiveLabel}`}
              sx={{
                borderRadius: 2.25,
                border: `1px solid ${flag.active ? `${flag.color}44` : brand.border}`,
                bgcolor: flag.active ? `${flag.color}0F` : brand.surfaceMuted,
                px: 1.25,
                py: 1.1,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: 1.5,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: brand.surface,
                    border: `1px solid ${flag.active ? `${flag.color}55` : brand.border}`,
                    color: flag.active ? flag.color : brand.textFaint,
                    flexShrink: 0,
                    mt: 0.15,
                  }}
                >
                  {icons[flag.key]}
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 900,
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        color: flag.active ? flag.color : brand.textFaint,
                      }}
                    >
                      {flag.title}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        flexShrink: 0,
                        fontWeight: 900,
                        borderRadius: 999,
                        px: 0.85,
                        py: 0.15,
                        border: `1px solid ${flag.active ? `${flag.color}55` : brand.border}`,
                        bgcolor: flag.active ? brand.surface : brand.surfaceSunken,
                        color: flag.active ? flag.color : brand.textFaint,
                      }}
                    >
                      {flag.active ? activeLabel : inactiveLabel}
                    </Typography>
                  </Stack>

                  <Typography
                    variant="body2"
                    sx={{
                      color: flag.active ? brand.textSecondary : brand.textFaint,
                      lineHeight: 1.55,
                      mt: 0.3,
                    }}
                  >
                    {flag.description}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>
    </Paper>
  );
}

function ScoreLine(props: {
  data: {
    type: ScoreType;
    title: string;
    label: string;
    explanation: string;
    value: number | null;
    updatedAt: string | null;
    status: string;
    message: string;
    recommendation: string;
    moduleRecommendations: ModuleRecommendation[];
    eksRecommendations?: EksRecommendation[];
  };
  updated: string;
  scoreLabel: string;
  showDetailsLabel: string;
  hideDetailsLabel: string;
  recommendedLabel: string;
  noRecommendedModulesLabel: string;
  openModuleLabel: string;
  completedLabel: string;
  priorityEksActionLabel: string;
  additionalEksActionsLabel: string;
  openAdditionalEksActionsLabel: string;
  hideAdditionalEksActionsLabel: string;
  noPriorityEksActionLabel: string;
  lang: "en" | "de";
  onOpenModule: (moduleOrder: number) => void;
}) {
  const {
    data,
    updated,
    scoreLabel,
    showDetailsLabel,
    hideDetailsLabel,
    recommendedLabel,
    noRecommendedModulesLabel,
    openModuleLabel,
    completedLabel,
    priorityEksActionLabel,
    additionalEksActionsLabel,
    openAdditionalEksActionsLabel,
    hideAdditionalEksActionsLabel,
    noPriorityEksActionLabel,
    lang,
    onOpenModule,
  } = props;

  const ready = data.value !== null;
  const normalizedValue = readinessPercent(data.value, data.type);
  const position = markerPosition(data.value, data.type);
  const scaleColor = ready ? colorOnScale(data.type, position) : "#D1D5DB";
  const eksRecommendations = data.eksRecommendations ?? [];
  const priorityEksRecommendations = eksRecommendations.filter((item) => item.is_priority);
  const regularEksRecommendations = eksRecommendations.filter((item) => !item.is_priority);
  const hasEksRecommendations = data.type === "E_Score" && eksRecommendations.length > 0;
  const [additionalEksOpen, setAdditionalEksOpen] = React.useState(false);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const detailsButtonLabel = detailsOpen ? hideDetailsLabel : showDetailsLabel;

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: "1px solid #E5E7EB",
        bgcolor: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      <Button
        fullWidth
        variant="text"
        aria-label={`${detailsButtonLabel}: ${data.title}`}
        aria-expanded={detailsOpen}
        onClick={() => setDetailsOpen((value) => !value)}
        endIcon={
          <Box
            aria-hidden
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              border: `1px solid ${brand.border}`,
              bgcolor: detailsOpen ? brand.ink : brand.surface,
              color: detailsOpen ? brand.onInkPrimary : brand.textSecondary,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              transition: "background-color 160ms ease, color 160ms ease",
            }}
          >
            {detailsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>
        }
        sx={{
          position: "relative",
          justifyContent: "space-between",
          alignItems: "center",
          textAlign: "left",
          textTransform: "none",
          color: brand.textPrimary,
          borderRadius: 0,
          pl: { xs: 1.75, sm: 2.5, md: 2.75 },
          pr: { xs: 1.25, sm: 2, md: 2.25 },
          py: { xs: 1.5, sm: 1.75 },
          "& .MuiButton-endIcon": { ml: { xs: 1, sm: 1.5 }, flexShrink: 0 },
          bgcolor: detailsOpen ? brand.surfaceMuted : brand.surface,
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: { xs: 4, md: 5 },
            bgcolor: ready ? scaleColor : brand.border,
          },
          "&:hover": { bgcolor: brand.surfaceMuted },
        }}
      >
        <Stack
          direction="row"
          spacing={{ xs: 1.25, sm: 1.75 }}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <ScoreRing value={ready ? normalizedValue : null} color={scaleColor} />

          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0.25, sm: 1 }}
              alignItems={{ xs: "flex-start", sm: "baseline" }}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  color: brand.textPrimary,
                  fontSize: { xs: 16, sm: 18 },
                  lineHeight: 1.25,
                }}
              >
                {data.label}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: brand.textMuted,
                  fontWeight: 900,
                  letterSpacing: 0.4,
                  flexShrink: 0,
                }}
              >
                {data.title}
              </Typography>
            </Stack>

            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                mt: 0.6,
                px: 1,
                py: 0.25,
                borderRadius: 999,
                border: `1px solid ${ready ? `${scaleColor}55` : brand.border}`,
                bgcolor: ready ? `${scaleColor}14` : brand.surfaceMuted,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: ready ? scaleColor : brand.textFaint,
                  fontWeight: 900,
                  fontSize: 13,
                }}
              >
                {data.status}
              </Typography>
            </Box>
          </Box>
        </Stack>

        <Box
          component="span"
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            p: 0,
            m: -1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          {detailsButtonLabel}
        </Box>
      </Button>

      <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
      <Box sx={{ px: { xs: 2, md: 2.25 }, pb: { xs: 2, md: 2.25 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Box sx={{ flex: 1.15, minWidth: 0 }}>
            <Typography variant="body2" sx={{ color: brand.textSecondary, lineHeight: 1.55 }}>
              {data.explanation}
            </Typography>

            <Typography
              sx={{
                fontWeight: 900,
                color: ready ? scaleColor : brand.textFaint,
                mt: 1.25,
              }}
            >
              {data.status}
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: brand.textMuted, lineHeight: 1.55, mt: 0.45 }}
            >
              {data.message}
            </Typography>
          </Box>

          <Box sx={{ flex: 1.35, minWidth: 0 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "flex-end" }}
              spacing={{ xs: 0.75, sm: 1.5 }}
              sx={{ mb: 1.4 }}
            >
              <Box>
                <Stack direction="row" spacing={0.75} alignItems="baseline">
                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: ready ? scaleColor : brand.textFaint,
                      fontSize: { xs: 28, sm: 34 },
                      lineHeight: 1,
                    }}
                  >
                    {ready ? formatPercent(normalizedValue) : "—"}
                  </Typography>
                </Stack>

                <Typography
                  variant="body2"
                  sx={{
                    color: ready ? "#6B7280" : "#9CA3AF",
                    fontWeight: 800,
                    mt: 0.45,
                  }}
                >
                  {scoreLabel}
                </Typography>
              </Box>

              <Typography
                variant="caption"
                sx={{ color: brand.textMuted, textAlign: { xs: "left", sm: "right" } }}
              >
                {updated}: {formatDateTime(data.updatedAt)}
              </Typography>
            </Stack>

            <ReadinessTrack
              position={ready ? position : null}
              color={scaleColor}
              figureLabel={data.title}
            />

            <Box
              sx={{
                mt: 1.5,
                borderRadius: 2.25,
                bgcolor: ready ? `${scaleColor}0F` : "#F9FAFB",
                border: "1px solid #E5E7EB",
                p: 1.25,
              }}
            >
              <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 900 }}>
                {recommendedLabel}
              </Typography>

              {hasEksRecommendations ? (
                <Stack spacing={1} sx={{ mt: 0.65 }}>
                  {priorityEksRecommendations.length > 0 ? (
                    <Stack spacing={0.8}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#B45309",
                          fontWeight: 900,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        {priorityEksActionLabel}
                      </Typography>

                      {priorityEksRecommendations.map((item) => (
                        <EksRecommendationCard
                          key={item.eks_module_code}
                          item={item}
                          lang={lang}
                          isPriority
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ color: "#6B7280", mt: 0.35, lineHeight: 1.5, fontWeight: 800 }}
                    >
                      {noPriorityEksActionLabel}
                    </Typography>
                  )}

                  {regularEksRecommendations.length > 0 && (
                    <Box
                      sx={{
                        borderRadius: 2,
                        bgcolor: "#FFFFFF",
                        border: "1px solid #E5E7EB",
                        overflow: "hidden",
                      }}
                    >
                      <Button
                        fullWidth
                        variant="text"
                        aria-label={
                          additionalEksOpen
                            ? hideAdditionalEksActionsLabel
                            : openAdditionalEksActionsLabel
                        }
                        onClick={() => setAdditionalEksOpen((value) => !value)}
                        endIcon={additionalEksOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{
                          justifyContent: "space-between",
                          textTransform: "none",
                          color: "#111827",
                          fontWeight: 900,
                          px: 1,
                          py: 0.85,
                          borderRadius: 0,
                        }}
                      >
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography sx={{ fontWeight: 900, fontSize: 14 }}>
                            {additionalEksActionsLabel}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              bgcolor: "#F3F4F6",
                              color: "#6B7280",
                              borderRadius: 999,
                              px: 0.85,
                              py: 0.2,
                              fontWeight: 900,
                            }}
                          >
                            {regularEksRecommendations.length}
                          </Typography>
                        </Stack>

                        <Box
                          component="span"
                          sx={{
                            position: "absolute",
                            width: 1,
                            height: 1,
                            p: 0,
                            m: -1,
                            overflow: "hidden",
                            clip: "rect(0 0 0 0)",
                            whiteSpace: "nowrap",
                            border: 0,
                          }}
                        >
                          {additionalEksOpen
                            ? hideAdditionalEksActionsLabel
                            : openAdditionalEksActionsLabel}
                        </Box>
                      </Button>

                      <Collapse in={additionalEksOpen} timeout="auto" unmountOnExit>
                        <Stack spacing={0.8} sx={{ p: 1, pt: 0 }}>
                          {regularEksRecommendations.map((item) => (
                            <EksRecommendationCard
                              key={item.eks_module_code}
                              item={item}
                              lang={lang}
                            />
                          ))}
                        </Stack>
                      </Collapse>
                    </Box>
                  )}
                </Stack>
              ) : data.moduleRecommendations.length > 0 ? (
                <Stack spacing={0.8} sx={{ mt: 0.65 }}>
                  {data.moduleRecommendations.map((module) => {
                    const moduleName = lang === "de" ? module.name_de : module.name_en;
                    const isCompleted = module.is_completed;

                    return (
                      <Box
                        key={`${data.type}-${module.module_order}`}
                        component="button"
                        type="button"
                        aria-label={`${openModuleLabel}: M${module.module_order} · ${moduleName}`}
                        onClick={() => onOpenModule(module.module_order)}
                        sx={{
                          width: "100%",
                          appearance: "none",
                          textAlign: "left",
                          borderRadius: 2,
                          bgcolor: isCompleted ? "#F0FDF4" : "#FFFFFF",
                          border: isCompleted ? "1px solid #86EFAC" : "1px solid #E5E7EB",
                          px: 1,
                          py: 0.85,
                          cursor: "pointer",
                          transition:
                            "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
                          "&:hover": {
                            borderColor: isCompleted ? "#22C55E" : "#9CA3AF",
                            boxShadow: "0 8px 18px rgba(17,24,39,0.08)",
                            transform: "translateY(-1px)",
                          },
                          "&:focus-visible": {
                            outline: "3px solid rgba(214,184,102,0.45)",
                            outlineOffset: 2,
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                color: isCompleted ? "#166534" : "#111827",
                                fontWeight: 900,
                                lineHeight: 1.35,
                              }}
                            >
                              M{module.module_order} · {moduleName}
                            </Typography>

                            {isCompleted && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#16A34A",
                                  fontWeight: 900,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                  mt: 0.25,
                                  display: "block",
                                }}
                              >
                                {completedLabel}
                              </Typography>
                            )}
                          </Box>

                          <ArrowForwardIcon
                            sx={{
                              color: isCompleted ? "#16A34A" : "#6B7280",
                              fontSize: 18,
                              flexShrink: 0,
                            }}
                          />
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: "#374151", mt: 0.35, lineHeight: 1.5 }}>
                  {ready ? noRecommendedModulesLabel : data.recommendation}
                </Typography>
              )}
            </Box>
          </Box>
        </Stack>
      </Box>
      </Collapse>
    </Box>
  );
}

function EksRecommendationCard(props: {
  item: EksRecommendation;
  lang: "en" | "de";
  isPriority?: boolean;
}) {
  const { item, lang, isPriority = false } = props;

  return (
    <Box
      sx={{
        borderRadius: 2,
        bgcolor: isPriority ? "#FFFBEB" : "#FFFFFF",
        border: isPriority ? "1px solid #FCD34D" : "1px solid #E5E7EB",
        px: 1,
        py: 0.85,
      }}
    >
      <Stack spacing={0.35}>
        <Typography
          sx={{
            color: isPriority ? "#92400E" : "#111827",
            fontWeight: 900,
            lineHeight: 1.35,
          }}
        >
          {getEksActionLabel(item.eks_module_code, lang)}
        </Typography>
      </Stack>
    </Box>
  );
}

const btnPrimarySx = {
  borderRadius: 2.5,
  textTransform: "none",
  fontWeight: 900,
  bgcolor: "#111827",
  boxShadow: "none",
  px: 2.2,
  py: 1.1,
  "&:hover": { bgcolor: "#0B1220", boxShadow: "none" },
} as const;

const btnOutlineSx = {
  borderRadius: 2.5,
  textTransform: "none",
  fontWeight: 900,
  borderColor: "#D1D5DB",
  color: "#111827",
  bgcolor: "#FFFFFF",
  px: 2.2,
  py: 1.1,
  "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F9FAFB" },
} as const;
